<script setup>
  import { ref, computed, watch, watchEffect } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import DataContainer from "@/components/DataContainer.vue";
  import ForecastTable from '@/components/ForecastTable.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";  
  import { useCookie } from "@/composables/useCookie.js";
  import { useUpdateKey } from '@/composables/useUpdateKey';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const forecastTable = defineModel('content');
  const emit = defineEmits(['zoomLevel:change', 'dailyMode:change', 'containerSettings:change']);

  const getSettings = () => forecastTable.value ? forecastTable.value.getSettings() : null;
  defineExpose({ getSettings });

  const props = defineProps({
    containerSettings: {
      type: Object,
      required: false
    },
    isFavorite: {
      type: Boolean,
      default: false
    },
    containerId: {
      type: [String, Number],
      required: false
    }
  })


  const spotId = props.isFavorite && props.containerSettings && Object.prototype.hasOwnProperty.call(props.containerSettings, 'spotId')
    ? parseInt(props.containerSettings.spotId)
    : null;  

  const cookie = useCookie();
  const spotStore = useSpotStore(spotId);
  const updateKey = useUpdateKey();

  if (props.isFavorite && spotId) {
    await spotStore.getSpotDetailById(spotId, { spot_types: "1,100,101" });
  }  

  if (props.isFavorite && props.containerSettings.lat && props.containerSettings.lon) {
    await spotStore.getSpotDetailByLatLon(`${props.containerSettings.lat}:${props.containerSettings.lon}`, { spot_types: "1,100,101" });
  }  

  // Persist between sessions using localStorage
  let savedTableSettings = $WEB_VIEW ? null : decodeURIComponent(cookie.get('FxTableSettings'));

  const spotInfo = computed(() => ({
    spotName: spotStore.spotName,
    spotId: spotStore.spotId,
    favSpotId: spotStore.spotFavoriteId,
    lat: spotStore.lat,
    lon: spotStore.lon,
    forecastModelData: {...spotStore.forecastModelData},
  }));  

  const forecastTableZoomLevel = ref(1);
  const dailyMode = ref('detailed');
  const selectedModelId = ref(null);

  if (!props.containerSettings && savedTableSettings && savedTableSettings !== 'undefined') {
    savedTableSettings = JSON.parse(savedTableSettings);

    forecastTableZoomLevel.value = parseInt(savedTableSettings.zoom);
    dailyMode.value = savedTableSettings.dailyMode;
  };

  watch(forecastTableZoomLevel, (newValue) => { 
    forecastTable.value?.updateZoom(newValue);
    emit('containerSettings:change');
  });

  watch(dailyMode, (newValue) => {  
    forecastTable.value?.updateDailyMode(newValue);
    emit('containerSettings:change');
  });
  
  watchEffect(() => {
    if (props.containerSettings) {
      if (Object.prototype.hasOwnProperty.call(props.containerSettings, 'zoom')) {
        forecastTableZoomLevel.value = parseInt(props.containerSettings.zoom);
      }
      else {
        forecastTableZoomLevel.value = 1;
      }

      // Legacy setting
      if (Object.prototype.hasOwnProperty.call(props.containerSettings, 'showMoreRows')) {
        dailyMode.value = props.containerSettings.showMoreRows === false ? 'basic' : 'detailed';
      }
      // Legacy setting
      else if (Object.prototype.hasOwnProperty.call(props.containerSettings, 'controlsMode')) {
        dailyMode.value = props.containerSettings.controlsMode === 'advance' ? 'detailed' : 'basic';
      } 
      else if (Object.prototype.hasOwnProperty.call(props.containerSettings, 'dailyMode')) {
        dailyMode.value = props.containerSettings.dailyMode;
      }
      else {
        dailyMode.value = 'detailed';
      }

      selectedModelId.value = props.containerSettings.modelId;
    };  
  });

  // need to use watchEffect to ensure the emit is called when component is first mounted
  // watch does not run until the value changes
  watchEffect(() => emit('zoomLevel:change', forecastTableZoomLevel.value));
  watchEffect(() => emit('dailyMode:change', dailyMode.value));

</script>

<template>
  <DataContainer style="width: 100%; height: 100%; flex: 1;">
    <div
      class="jwx-data-container__toolbar"
      ref="toolbarContainer"
    >
      <SegmentedControl
        v-model.number="forecastTableZoomLevel"
        label="Select the time period length to display on the forecast table."
        style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px;"
      >
        <SegmentedControlButton :value="1">Daily</SegmentedControlButton>
        <SegmentedControlButton :value="7">7 Day</SegmentedControlButton>
      </SegmentedControl>

      <SegmentedControl
        v-if="forecastTableZoomLevel === 1"
        v-model="dailyMode"
        label="Choose to display the Basic or Detailed Forecast Table view."
        style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px; margin-left: auto;"
      >
        <SegmentedControlButton value="basic">Basic</SegmentedControlButton>
        <SegmentedControlButton value="detailed">Detailed</SegmentedControlButton>
      </SegmentedControl>

    </div>

    <ForecastTable
      v-model="forecastTable"
      :spotInfo="spotInfo"
      :selectedModelId="selectedModelId"
      :useSavedState="!$WEB_VIEW && !props.isFavorite"
      :updateKey="updateKey.key.value"
      :containerId="props.containerId"
     />
  </DataContainer>
</template>